import { c as _c } from "react/compiler-runtime";
import { getCountryNameFromCode } from '@onefootprint/core';
import { IcoArrowRightSmall16, IcoGlobe16, IcoPinMarker16 } from '@onefootprint/icons';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { ApiNodeDataIdentityDocument, IdDocInput, IdDocInputKind, IdDocKind, IdDocOutput } from '@onefootprint/request-types/dashboard';
import { Flag, Tooltip } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import type { NodeProps } from '@/components/playbook-builder/playbook-builder.types';
import useDocListText from '@/hooks/use-doc-list-text';
import Item from '@/playbook-builder/components/item';
import { getDocumentScope } from '../id-doc-kinds';
type IdDocsViewProps = {
  data: Pick<NodeProps<ApiNodeDataIdentityDocument>['data'], 'collectSelfie' | 'documentTypesAndCountries' | 'uploadSettings'> & {
    /** Optional because the legacy collect_document node reuses this view and has no outputs. */
    outputs?: IdDocOutput[];
    inputs?: IdDocInput[];
  };
};
const IdDocsView = t0 => {
  const $ = _c(75);
  const {
    data: t1
  } = t0;
  const {
    collectSelfie,
    documentTypesAndCountries: t2,
    uploadSettings,
    outputs,
    inputs
  } = t1;
  const {
    countrySpecific,
    global
  } = t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "collectDocument"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t3);
  let t4;
  if ($[1] !== global) {
    t4 = getDocumentScope({
      global
    });
    $[1] = global;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const isAnyCountry = t4 === "any_country";
  let t5;
  if ($[3] !== countrySpecific) {
    t5 = Object.entries(countrySpecific) as Array<[Iso3166TwoDigitCountryCode, IdDocKind[]]> || [];
    $[3] = countrySpecific;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const country = t5;
  let t6;
  if ($[5] !== t) {
    t6 = t(_temp);
    $[5] = t;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <div className="text-label-2 text-primary">{t6}</div>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isAnyCountry) {
    t8 = isAnyCountry ? <IcoGlobe16 color="tertiary" /> : <IcoPinMarker16 color="tertiary" />;
    $[9] = isAnyCountry;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isAnyCountry || $[12] !== t) {
    t9 = isAnyCountry ? t(_temp2) : t(_temp3);
    $[11] = isAnyCountry;
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <div className="flex items-center gap-2 text-label-3 text-primary">{t8}{t9}</div>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== global || $[18] !== isAnyCountry) {
    t11 = isAnyCountry && <DocumentKinds docs={global} />;
    $[17] = global;
    $[18] = isAnyCountry;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div className="flex flex-col gap-2 p-3 border border-tertiary rounded-md">{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7) {
    t13 = <section className="flex flex-col gap-3">{t7}{t12}</section>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isAnyCountry || $[27] !== t) {
    t14 = isAnyCountry ? t(_temp4) : t(_temp5);
    $[26] = isAnyCountry;
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <div className="text-label-2 text-primary">{t14}</div>;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isAnyCountry || $[32] !== t) {
    t16 = isAnyCountry ? t(_temp6) : t(_temp7);
    $[31] = isAnyCountry;
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div className="text-body-2 text-tertiary">{t16}</div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t17) {
    t18 = <div className="flex flex-col gap-1">{t15}{t17}</div>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== country || $[40] !== t) {
    t19 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">{country.length === 0 ? <p className="text-body-2 text-tertiary">{t(_temp8)}</p> : <ul className="flex flex-col gap-4">{country.map(_temp9)}</ul>}</div>;
    $[39] = country;
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t18 || $[43] !== t19) {
    t20 = <section className="flex flex-col gap-3">{t18}{t19}</section>;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t(_temp10);
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <div className="text-label-2 text-primary">{t21}</div>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t(_temp11);
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== collectSelfie || $[52] !== t23) {
    t24 = <Item label={t23} value={collectSelfie} />;
    $[51] = collectSelfie;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t) {
    t25 = t(_temp12);
    $[54] = t;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = uploadSettings === "require_mobile_capture";
  let t27;
  if ($[56] !== t25 || $[57] !== t26) {
    t27 = <Item label={t25} value={t26} />;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t24 || $[60] !== t27) {
    t28 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md"><ul className="flex flex-col gap-2">{t24}{t27}</ul></div>;
    $[59] = t24;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t22 || $[63] !== t28) {
    t29 = <section className="flex flex-col gap-3">{t22}{t28}</section>;
    $[62] = t22;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== inputs) {
    t30 = !!inputs?.length && <DocumentInputs inputs={inputs} />;
    $[65] = inputs;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== outputs) {
    t31 = !!outputs?.length && <Outputs outputs={outputs} />;
    $[67] = outputs;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t13 || $[70] !== t20 || $[71] !== t29 || $[72] !== t30 || $[73] !== t31) {
    t32 = <div className="flex flex-col gap-4">{t13}{t20}{t29}{t30}{t31}</div>;
    $[69] = t13;
    $[70] = t20;
    $[71] = t29;
    $[72] = t30;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  return t32;
};
const DocumentInputs = t0 => {
  const $ = _c(25);
  const {
    inputs
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectDocument.inputs"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp13);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp14);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp15);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      front_image: t2,
      back_image: t3,
      selfie_image: t4
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const labelByKind = t5;
  let t6;
  if ($[11] !== t) {
    t6 = t(_temp16);
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <div className="text-label-2 text-primary">{t6}</div>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== inputs || $[16] !== labelByKind) {
    let t9;
    if ($[18] !== labelByKind) {
      t9 = t10 => {
        const {
          kind,
          expression
        } = t10;
        return <div className="flex gap-2 items-center text-body-2" key={kind}><span className="text-tertiary">{expression}</span><IcoArrowRightSmall16 className="flex-shrink-0" />{labelByKind[kind]}</div>;
      };
      $[18] = labelByKind;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = inputs.map(t9);
    $[15] = inputs;
    $[16] = labelByKind;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t9) {
    t10 = <section className="flex flex-col gap-3">{t7}{t9}</section>;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
const Outputs = t0 => {
  const $ = _c(42);
  const {
    outputs
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "nodeOutputs"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "collectDocument.outputs.kinds"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tKinds
  } = useTranslation("playbook-builder", t2);
  let t3;
  if ($[2] !== tKinds) {
    t3 = tKinds(_temp17);
    $[2] = tKinds;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = ["age", t3];
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== tKinds) {
    t5 = tKinds(_temp18);
    $[6] = tKinds;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = ["dob", t5];
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== tKinds) {
    t7 = tKinds(_temp19);
    $[10] = tKinds;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = ["issuing_country", t7];
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== tKinds) {
    t9 = tKinds(_temp20);
    $[14] = tKinds;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = ["risk_signals", t9];
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== tKinds) {
    t11 = tKinds(_temp21);
    $[18] = tKinds;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = ["ocr_data", t11];
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12 || $[24] !== t4 || $[25] !== t6 || $[26] !== t8) {
    t13 = new Map([t4, t6, t8, t10, t12]);
    $[22] = t10;
    $[23] = t12;
    $[24] = t4;
    $[25] = t6;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const labelByKind = t13;
  let t14;
  if ($[28] !== t) {
    t14 = t(_temp22);
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <div className="text-label-2 text-primary">{t14}</div>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== labelByKind || $[33] !== outputs) {
    let t17;
    if ($[35] !== labelByKind) {
      t17 = t18 => {
        const {
          kind,
          onboardingDataKey
        } = t18;
        return <div className="flex gap-2 items-center text-body-2" key={kind}>{labelByKind.get(kind) ?? kind}<IcoArrowRightSmall16 className="flex-shrink-0" /><span className="text-tertiary">data.{onboardingDataKey}</span></div>;
      };
      $[35] = labelByKind;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    t16 = outputs.map(t17);
    $[32] = labelByKind;
    $[33] = outputs;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">{t16}</div>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <section className="flex flex-col gap-3">{t15}{t17}</section>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
const DocumentKinds = t0 => {
  const $ = _c(5);
  const {
    docs: t1,
    limit: t2
  } = t0;
  const docs = t1 === undefined ? [] : t1;
  const limit = t2 === undefined ? 2 : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "collectDocument"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t3);
  const list = useDocListText()(docs);
  const firstPart = list.slice(0, limit);
  const lastPart = list.slice(limit);
  const shouldCut = list.length > limit;
  if (docs.length === 0) {
    let t4;
    if ($[1] !== t) {
      t4 = t(_temp23);
      $[1] = t;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== t4) {
      t5 = <p className="text-body-2 text-tertiary">{t4}</p>;
      $[3] = t4;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  return shouldCut ? <div className="flex gap-1 items-center"><p className="text-body-2 text-tertiary">{firstPart.join(", ")}</p><p className="text-body-2 text-tertiary">{t(_temp24)}</p><Tooltip disabled={!lastPart.length} content={lastPart.join(", ")}><p className="underline whitespace-nowrap text-body-2 text-tertiary">{t(_temp25, {
          count: lastPart.length
        })}</p></Tooltip></div> : <p className="text-body-2 text-tertiary">{list.join(", ")}</p>;
};
export default IdDocsView;
function _temp($) {
  return $.acceptedDocuments.title;
}
function _temp2($_0) {
  return $_0.acceptedDocuments.scope.anyCountry.label;
}
function _temp3($_1) {
  return $_1.acceptedDocuments.scope.specificCountries.label;
}
function _temp4($_2) {
  return $_2.acceptedDocuments.exceptions.title;
}
function _temp5($_3) {
  return $_3.acceptedDocuments.countries.title;
}
function _temp6($_4) {
  return $_4.acceptedDocuments.exceptions.hint;
}
function _temp7($_5) {
  return $_5.acceptedDocuments.countries.hint;
}
function _temp8($_6) {
  return $_6.noDocuments;
}
function _temp9(t0) {
  const [code, docs] = t0;
  return <li className="flex flex-col gap-1 justify-between" key={code}><div className="flex gap-1 justify-self-center items-center text-body-2 text-tertiary"><Flag code={code} />{getCountryNameFromCode(code)}</div><div><DocumentKinds docs={docs as IdDocKind[]} /></div></li>;
}
function _temp10($_7) {
  return $_7.extraRequirements;
}
function _temp11($_8) {
  return $_8.requestSelfie;
}
function _temp12($_9) {
  return $_9.requireMobileCapture;
}
function _temp13($) {
  return $.documents.sides.front;
}
function _temp14($_0) {
  return $_0.documents.sides.back;
}
function _temp15($_1) {
  return $_1.documents.sides.selfie;
}
function _temp16($_2) {
  return $_2.title;
}
function _temp17($) {
  return $.age;
}
function _temp18($_0) {
  return $_0.dob;
}
function _temp19($_1) {
  return $_1.issuingCountry;
}
function _temp20($_2) {
  return $_2.riskSignals;
}
function _temp21($_3) {
  return $_3.ocrData;
}
function _temp22($_4) {
  return $_4.title;
}
function _temp23($) {
  return $.noDocuments;
}
function _temp24($_0) {
  return $_0.andLowercase;
}
function _temp25($_1) {
  return $_1.more;
}